import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export default api;

// Normalise FastAPI's various error shapes (string / 422-array / object)
// into one string. When `detail` is missing/null this used to return the
// generic "Etwas ist schiefgelaufen…" placeholder, which hid the actual
// HTTP status (520, 502, 504, network errors) from the user AND from us
// during Prod incident triage. `describeApiError()` below is the preferred
// replacement — it always returns something actionable.
export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Build a user-facing error message that ALWAYS includes the HTTP status
// and endpoint context, even when the response has no JSON body (Cloudflare
// error pages, timeouts, network failures). This is the function every
// upload-flow catch-site should use — see /pages/AdminUpload.jsx.
//
// Usage:
//   const resp = await fetch(url);
//   if (!resp.ok) throw new Error(await describeApiError(resp, "POST /complete"));
//
// For a `Response` object it reads `resp.status`, tries to parse JSON for
// `detail`, and falls back to text() if the body is HTML. For a plain
// `Error` (e.g. thrown by fetch on network failure) it just prefixes the
// endpoint. Never returns null.
export async function describeApiError(respOrError, endpoint) {
  const ep = endpoint || "API";
  // Network-level fetch failure (no Response object at all).
  if (respOrError instanceof Error) {
    return `${ep}: ${respOrError.message || "Netzwerkfehler"}`;
  }
  const status = respOrError?.status ?? "?";
  const statusText = respOrError?.statusText || "";
  let detail = null;
  try {
    // Try JSON first (FastAPI). Cloudflare returns HTML for 520/502/504.
    const body = await respOrError.clone().json();
    detail = formatApiErrorDetail(body?.detail);
  } catch {
    try {
      const txt = (await respOrError.clone().text()).slice(0, 240).trim();
      if (txt && !txt.startsWith("<")) detail = txt;
    } catch {
      // ignore — nothing readable
    }
  }
  // Give Cloudflare-shaped 5xx a concrete hint so the user (and we) know
  // it is a gateway timeout, not a backend bug.
  const cfHint =
    status === 520
      ? "Cloudflare-Timeout — Backend hat länger als 100 Sek. gebraucht"
      : status === 502
      ? "Bad Gateway — Backend nicht erreichbar"
      : status === 504
      ? "Gateway Timeout — Backend antwortet nicht"
      : null;
  const parts = [`${ep} → HTTP ${status}${statusText ? " " + statusText : ""}`];
  if (cfHint) parts.push(cfHint);
  if (detail) parts.push(detail);
  return parts.join(" · ");
}
